import { Image as ExpoImage } from 'expo-image';
import { router } from 'expo-router';
import { MessageCircle, Minimize2, Share2, X } from 'lucide-react-native';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type ViewToken,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/src/components/Button';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { useQueuedPostReaction } from '@/src/features/offline/use-outbox';
import {
  useFollowState,
  useToggleFollow,
} from '@/src/features/profile/use-profile';
import {
  nativeShareDescription,
  nativeShareTitle,
  ShareOptionsModal,
} from '@/src/features/sharing/ShareOptionsModal';
import {
  openFacebookShareDialog,
  shareLinkWithDevice,
} from '@/src/features/sharing/share-link';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { formatDate } from '@/src/utils/dates';
import { openUserProfile } from '@/src/utils/profile-navigation';

import {
  applyReactionTransition,
  CompactReactionButton,
  createReactionMutationId,
  ReactionDetailsModal,
  reactionMeta,
  shouldClearOptimisticReaction,
} from './ReactionControls';
import {
  ProfileMentionInput,
  visibleProfileMentions,
} from './ProfileMentionInput';
import { ProfileMentionText } from './ProfileMentionText';
import {
  createPost,
  emptyReactionCounts,
  MAX_POST_BODY_LENGTH,
  recordPostShare,
  type PostMediaItem,
  type ProfileMention,
  type PublicPost,
  type Reaction,
  type ReactionCounts,
} from './post-service';
import { getTopUniqueReactions } from './reaction-summary';
import { ZoomablePhoto } from './ZoomablePhoto';
import { usePostReaction, useSetPostReaction } from './use-post-like';

type OptimisticReactionState = {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  mutationId: string;
  reaction: Reaction | null;
};

type SharePostAction = 'copy' | 'facebook' | 'native' | 'reshare';
type SharePostMethod = 'clipboard' | 'facebook' | 'native' | 'reshare' | 'web';

function photoPostsFromPosts(
  posts: PublicPost[],
  initialPost?: PublicPost | null,
) {
  const photoPosts = posts.filter((post) => (post.mediaItems ?? []).length > 0);

  if (!initialPost || (initialPost.mediaItems ?? []).length === 0) {
    return photoPosts;
  }

  const initialIndex = photoPosts.findIndex(
    (post) => post.id === initialPost.id,
  );

  if (initialIndex >= 0) {
    return [
      photoPosts[initialIndex],
      ...photoPosts.slice(initialIndex + 1),
      ...photoPosts.slice(0, initialIndex),
    ];
  }

  return [
    initialPost,
    ...photoPosts.filter((post) => post.id !== initialPost.id),
  ];
}

function PhotoSlide({
  activePhotoIndex,
  height,
  onZoomChange,
  onPhotoIndexChange,
  post,
  width,
}: {
  activePhotoIndex: number;
  height: number;
  onZoomChange?: (zoomed: boolean) => void;
  onPhotoIndexChange: (index: number) => void;
  post: PublicPost;
  width: number;
}) {
  const [zoomed, setZoomed] = useState(false);
  const scrollRef = useRef<ScrollView | null>(null);
  const scrollReportedIndexRef = useRef<number | null>(null);
  const pendingProgrammaticIndexRef = useRef<number | null>(activePhotoIndex);
  const photos = (post.mediaItems ?? []).filter(
    (item) => item.kind === 'image',
  );

  function updatePhotoIndexFromOffset(offsetX: number) {
    if (!width) {
      return;
    }

    const nextIndex = Math.max(
      0,
      Math.min(photos.length - 1, Math.round(offsetX / width)),
    );
    const pendingIndex = pendingProgrammaticIndexRef.current;

    if (pendingIndex != null) {
      if (nextIndex !== pendingIndex) {
        return;
      }

      pendingProgrammaticIndexRef.current = null;
    }

    scrollReportedIndexRef.current = nextIndex;
    onPhotoIndexChange(nextIndex);
  }

  useLayoutEffect(() => {
    setZoomed(false);
    onZoomChange?.(false);

    if (scrollReportedIndexRef.current === activePhotoIndex) {
      scrollReportedIndexRef.current = null;
      return;
    }

    requestAnimationFrame(() => {
      pendingProgrammaticIndexRef.current = activePhotoIndex;
      scrollRef.current?.scrollTo({
        animated: false,
        x: activePhotoIndex * width,
      });
    });
  }, [activePhotoIndex, onZoomChange, post.id, width]);

  function handleZoomChange(nextZoomed: boolean) {
    setZoomed(nextZoomed);
    onZoomChange?.(nextZoomed);
  }

  return (
    <View style={[styles.slide, { height, width }]}>
      <ScrollView
        ref={scrollRef}
        horizontal
        onMomentumScrollEnd={(event) => {
          updatePhotoIndexFromOffset(event.nativeEvent.contentOffset.x);
        }}
        onScroll={(event) => {
          updatePhotoIndexFromOffset(event.nativeEvent.contentOffset.x);
        }}
        pagingEnabled
        scrollEnabled={!zoomed}
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
      >
        {photos.map((photo: PostMediaItem, index) => (
          <View key={photo.id} style={[styles.photoPage, { width }]}>
            <ZoomablePhoto
              accessibilityLabel={`Post photo ${index + 1}`}
              captureDesktopPinch={
                index === activePhotoIndex && Boolean(onZoomChange)
              }
              onZoomChange={handleZoomChange}
              sourceURL={photo.thumbnail?.downloadURL ?? photo.downloadURL}
              style={styles.photo}
            />
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

function ResharePhotoComposeModal({
  body,
  currentUid,
  mentions,
  onBodyChange,
  onClose,
  onMentionsChange,
  onSubmit,
  post,
  posting,
  visible,
}: {
  body: string;
  currentUid?: string;
  mentions: ProfileMention[];
  onBodyChange: (body: string) => void;
  onClose: () => void;
  onMentionsChange: (mentions: ProfileMention[]) => void;
  onSubmit: () => void;
  post: PublicPost;
  posting: boolean;
  visible: boolean;
}) {
  const author = post.authorFullName ?? post.authorUsername ?? 'Frienzzi user';

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable
        disabled={posting}
        onPress={onClose}
        style={styles.composeBackdrop}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={styles.composeCard}
        >
          <View style={styles.composeHeader}>
            <View style={styles.composeTitleBlock}>
              <Text style={styles.composeTitle}>Reshare on Frienzzi</Text>
              <Text style={styles.composeSubtitle}>
                Add your own message or reshare these photos as-is.
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close reshare composer"
              accessibilityRole="button"
              disabled={posting}
              onPress={onClose}
              style={({ pressed }) => [
                styles.composeCloseButton,
                pressed && !posting ? styles.overlayButtonPressed : null,
              ]}
            >
              <X color={colors.mutedText} size={20} strokeWidth={2.5} />
            </Pressable>
          </View>
          <ScrollView
            contentContainerStyle={styles.composeScrollContent}
            keyboardShouldPersistTaps="handled"
            style={styles.composeScroll}
          >
            <ProfileMentionInput
              label="Your message"
              autoCapitalize="sentences"
              maxLength={MAX_POST_BODY_LENGTH}
              mentions={mentions}
              multiline
              numberOfLines={4}
              onChangeText={onBodyChange}
              onMentionsChange={onMentionsChange}
              placeholder="Add a thought..."
              scope={{ kind: 'post' }}
              style={styles.composeInput}
              textAlignVertical="top"
              value={body}
            />
            <Text style={styles.composeCounter}>
              {body.length}/{MAX_POST_BODY_LENGTH}
            </Text>
            <View style={styles.resharePreview}>
              <Text style={styles.resharePreviewEyebrow}>Photo post</Text>
              <Text numberOfLines={1} style={styles.resharePreviewTitle}>
                {author}
              </Text>
              {post.body ? (
                <ProfileMentionText
                  body={post.body}
                  currentUid={currentUid}
                  mentions={post.profileMentions ?? []}
                  source="post_photo_reshare_preview"
                  style={styles.resharePreviewBody}
                />
              ) : (
                <Text style={styles.resharePreviewBody}>
                  This reshare will include the original photos.
                </Text>
              )}
            </View>
          </ScrollView>
          <View style={styles.composeActions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={posting}
              style={styles.composeButton}
              onPress={onClose}
            />
            <Button
              title="Reshare"
              loading={posting}
              loadingTitle="Resharing..."
              style={styles.composeButton}
              onPress={onSubmit}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function PhotoActions({
  currentUid,
  onActorProfilePress,
  onCommentPress,
  onOpenShare,
  onReaction,
  post,
  reactionCounts,
  reactionDisabled,
  reactionLoading,
  selectedReaction,
  shareCount,
  totalReactions,
}: {
  currentUid?: string;
  onActorProfilePress?: (targetUid: string) => void;
  onCommentPress: () => void;
  onOpenShare: () => void;
  onReaction: (reaction: Reaction) => void;
  post: PublicPost;
  reactionCounts: ReactionCounts;
  reactionDisabled: boolean;
  reactionLoading: boolean;
  selectedReaction: Reaction | null;
  shareCount: number;
  totalReactions: number;
}) {
  const [reactionDetailsVisible, setReactionDetailsVisible] = useState(false);
  const topReactionIcons = getTopUniqueReactions(reactionCounts);

  return (
    <View style={styles.actionsRail}>
      <View style={styles.actionCluster}>
        <View style={styles.railIconCircle}>
          <CompactReactionButton
            disabled={reactionDisabled}
            loading={reactionLoading}
            pickerOpensLeft
            selectedReaction={selectedReaction}
            onSelect={onReaction}
          />
        </View>
        <Text style={styles.actionCount}>{totalReactions}</Text>
      </View>
      <Pressable
        accessibilityLabel="View comments"
        accessibilityRole="button"
        onPress={onCommentPress}
        style={({ pressed }) => [
          styles.railButton,
          pressed ? styles.railButtonPressed : null,
        ]}
      >
        <View style={styles.railIconCircle}>
          <MessageCircle color={colors.surface} size={25} strokeWidth={2.4} />
        </View>
        <Text style={styles.actionCount}>{post.commentCount}</Text>
      </Pressable>
      {post.visibility === 'public' ? (
        <Pressable
          accessibilityLabel="Share post"
          accessibilityRole="button"
          onPress={onOpenShare}
          style={({ pressed }) => [
            styles.railButton,
            pressed ? styles.railButtonPressed : null,
          ]}
        >
          <View style={styles.railIconCircle}>
            <Share2 color={colors.surface} size={24} strokeWidth={2.4} />
          </View>
          <Text style={styles.actionCount}>{shareCount}</Text>
        </Pressable>
      ) : null}
      {topReactionIcons.length > 0 ? (
        <>
          <Pressable
            accessibilityLabel="View reactions"
            accessibilityRole="button"
            onPress={() => {
              if (!currentUid) {
                router.push('/sign-in');
                return;
              }

              setReactionDetailsVisible(true);
            }}
            style={({ pressed }) => [
              styles.topReactionButton,
              pressed ? styles.railButtonPressed : null,
            ]}
          >
            <View style={styles.topReactionStack}>
              {topReactionIcons.map((reaction, index) => (
                <View
                  key={`${reaction}-${index}`}
                  style={[
                    styles.topReactionBubble,
                    index > 0 ? styles.topReactionBubbleOverlap : null,
                  ]}
                >
                  <Text style={styles.topReactionEmoji}>
                    {reactionMeta[reaction].emoji}
                  </Text>
                </View>
              ))}
            </View>
          </Pressable>
          {currentUid ? (
            <ReactionDetailsModal
              counts={reactionCounts}
              currentUid={currentUid}
              target={{ kind: 'post', postId: post.id }}
              visible={reactionDetailsVisible}
              onActorProfilePress={onActorProfilePress}
              onClose={() => setReactionDetailsVisible(false)}
            />
          ) : null}
        </>
      ) : null}
    </View>
  );
}

function PhotoOverlay({
  actionsVisible,
  captionExpanded,
  currentUid,
  photoCount,
  photoIndex,
  onClose,
  onMinimize,
  onOpenAuthor,
  onToggleCaption,
  post,
}: {
  actionsVisible: boolean;
  captionExpanded: boolean;
  currentUid?: string;
  photoCount: number;
  photoIndex: number;
  onClose: () => void;
  onMinimize: () => void;
  onOpenAuthor: () => void;
  onToggleCaption: () => void;
  post: PublicPost;
}) {
  const { showError, showSuccess } = useToast();
  const followState = useFollowState(currentUid, post.authorUid);
  const toggleFollow = useToggleFollow(currentUid, post.authorUid);
  const authorFullName = post.authorFullName || 'Frienzzi user';
  const authorUsername = post.authorUsername;
  const authorPhotoURL = post.authorPhotoURL;
  const authorVerifiedBadge = post.authorVerifiedBadge === true;
  const authorFounderBadge = post.authorFounderBadge === true;
  const hasLongCaption = Boolean(post.body && post.body.length > 150);
  const canFollow = Boolean(currentUid && currentUid !== post.authorUid);
  const followActionPending = followState.loading || toggleFollow.isPending;

  function toggleAuthorFollow() {
    if (!currentUid) {
      router.push('/sign-in');
      return;
    }

    if (!canFollow || followActionPending) {
      return;
    }

    const nextFollowing = !followState.following;
    trackEvent('follow_toggle', {
      following: nextFollowing,
      result: 'started',
      source: 'post_photo_viewer',
    });
    toggleFollow.mutate(
      { following: nextFollowing },
      {
        onError: (error) => {
          showError(error, 'Unable to update follow.');
        },
        onSuccess: (result) => {
          if (result === 'queued') {
            showSuccess('Follow update queued.');
            return;
          }

          showSuccess(
            nextFollowing
              ? `You are now following ${authorFullName}.`
              : `You unfollowed ${authorFullName}.`,
            nextFollowing ? 'Following' : 'Unfollowed',
          );
        },
      },
    );
  }

  return (
    <>
      <View pointerEvents="box-none" style={styles.topOverlay}>
        <View style={styles.topLeftActions}>
          <Pressable
            accessibilityLabel="Close photo viewer"
            accessibilityRole="button"
            onPress={onClose}
            style={({ pressed }) => [
              styles.overlayButton,
              pressed ? styles.overlayButtonPressedDark : null,
            ]}
          >
            <X color={colors.surface} size={24} strokeWidth={2.5} />
          </Pressable>
          <Pressable
            accessibilityLabel="Minimize photos"
            accessibilityRole="button"
            onPress={onMinimize}
            style={({ pressed }) => [
              styles.overlayButton,
              pressed ? styles.overlayButtonPressedDark : null,
            ]}
          >
            <Minimize2 color={colors.surface} size={21} strokeWidth={2.5} />
          </Pressable>
        </View>
        {photoCount > 1 ? (
          <View style={styles.photoCounterButton}>
            <Text style={styles.photoCounterText}>
              {photoIndex + 1}/{photoCount}
            </Text>
          </View>
        ) : null}
      </View>
      <View pointerEvents="box-none" style={styles.bottomContent}>
        <View
          style={[
            styles.bottomOverlay,
            !actionsVisible ? styles.bottomOverlayNoActions : null,
          ]}
        >
          <View style={styles.authorRow}>
            <IdentityHeader
              avatarSize={44}
              containerStyle={styles.viewerIdentity}
              currentUid={currentUid}
              density="viewer"
              founderBadge={authorFounderBadge}
              fullName={authorFullName}
              metadata={[
                {
                  id: 'created',
                  kind: 'text',
                  label: formatDate(post.createdAt),
                },
              ]}
              photoURL={authorPhotoURL}
              uid={post.authorUid}
              username={authorUsername}
              variant="overlay"
              verifiedBadge={authorVerifiedBadge}
              onPress={onOpenAuthor}
            />
            {canFollow ? (
              <Pressable
                accessibilityLabel={
                  followState.following
                    ? `Unfollow ${authorFullName}`
                    : `Follow ${authorFullName}`
                }
                accessibilityRole="button"
                disabled={followActionPending}
                onPress={toggleAuthorFollow}
                style={({ pressed }) => [
                  styles.followButton,
                  followState.following ? styles.followButtonFollowing : null,
                  pressed && !followActionPending
                    ? styles.followButtonPressed
                    : null,
                  followActionPending ? styles.followButtonDisabled : null,
                ]}
              >
                {followActionPending ? (
                  <ActivityIndicator color={colors.surface} size="small" />
                ) : (
                  <Text
                    style={[
                      styles.followButtonText,
                      followState.following
                        ? styles.followButtonTextFollowing
                        : null,
                    ]}
                  >
                    {followState.following ? 'Following' : 'Follow'}
                  </Text>
                )}
              </Pressable>
            ) : null}
          </View>
          {post.body ? (
            <Pressable
              accessibilityRole="button"
              disabled={!hasLongCaption}
              onPress={onToggleCaption}
              style={styles.captionPressable}
            >
              <ScrollView
                scrollEnabled={captionExpanded}
                showsVerticalScrollIndicator={captionExpanded}
                style={[
                  styles.captionScroll,
                  captionExpanded ? styles.captionScrollExpanded : null,
                ]}
              >
                <ProfileMentionText
                  body={post.body}
                  currentUid={currentUid}
                  mentionStyle={styles.viewerCaptionMention}
                  mentions={post.profileMentions ?? []}
                  numberOfLines={captionExpanded ? undefined : 2}
                  source="photo_viewer_caption"
                  style={styles.viewerCaption}
                />
              </ScrollView>
              {hasLongCaption ? (
                <Text style={styles.moreText}>
                  {captionExpanded ? 'Show less' : 'Show more'}
                </Text>
              ) : null}
            </Pressable>
          ) : null}
        </View>
      </View>
    </>
  );
}

function ActivePhotoControls({
  onActorProfilePress,
  onCommentPress,
  onPostEngagement,
  post,
}: {
  onActorProfilePress?: (targetUid: string) => void;
  onCommentPress: (post: PublicPost) => void;
  onPostEngagement?: (post: PublicPost) => void;
  post: PublicPost;
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const reactionState = usePostReaction(post.id, user?.uid);
  const queuedReaction = useQueuedPostReaction({
    postId: post.id,
    uid: user?.uid,
  });
  const setReaction = useSetPostReaction(user?.uid);
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticReactionState | null>(null);
  const [optimisticShareCount, setOptimisticShareCount] = useState<
    number | null
  >(null);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sharingAction, setSharingAction] = useState<SharePostAction | null>(
    null,
  );
  const [reshareComposerVisible, setReshareComposerVisible] = useState(false);
  const [reshareBody, setReshareBody] = useState('');
  const [reshareMentions, setReshareMentions] = useState<ProfileMention[]>([]);
  const sharingRef = useRef(false);
  const baseReactionCounts = post.reactionCounts ?? emptyReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const queuedCounts =
    queuedReaction !== undefined && !optimisticCounts
      ? applyReactionTransition({
          baseCounts: baseReactionCounts,
          baseReaction: reactionState.reaction,
          reaction: queuedReaction,
        })
      : null;
  const visibleReaction = optimisticReaction
    ? optimisticReaction.reaction
    : queuedReaction !== undefined
      ? queuedReaction
      : reactionState.reaction;
  const visibleReactionCounts =
    optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts;
  const totalVisibleReactions = Object.values(visibleReactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const visibleShareCount = optimisticShareCount ?? post.shareCount;
  const canOpenShareMenu =
    post.visibility === 'public' && Boolean(user?.uid) && !sharing;
  const canResharePost = post.visibility === 'public' && Boolean(user?.uid);
  const shareUrl = `https://frienzzi.com/posts/${post.id}`;
  const authorFullName = post.authorFullName || 'Frienzzi user';

  useEffect(() => {
    setOptimisticReaction(null);
    setOptimisticShareCount(null);
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
    setShareMenuVisible(false);
    setReshareComposerVisible(false);
    setReshareBody('');
    setReshareMentions([]);
  }, [post.id, user?.uid]);

  useEffect(() => {
    if (reactionState.error) {
      showError(reactionState.error, 'Unable to load your post reaction.');
    }
  }, [reactionState.error, showError]);

  useEffect(() => {
    if (
      optimisticReaction &&
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        queuedReaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    queuedReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  useEffect(() => {
    if (
      optimisticShareCount != null &&
      post.shareCount >= optimisticShareCount
    ) {
      setOptimisticShareCount(null);
    }
  }, [optimisticShareCount, post.shareCount]);

  function chooseReaction(reaction: Reaction) {
    if (!user?.uid || reactionState.loading) {
      if (!user?.uid) {
        router.push('/sign-in');
      }
      return;
    }

    const nextReaction = visibleReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();

    setOptimisticReaction({
      baseCounts:
        optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts,
      baseReaction: visibleReaction,
      mutationId,
      reaction: nextReaction,
    });

    setReaction.mutate(
      { mutationId, post, reaction: nextReaction },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that post reaction.');
        },
        onSuccess: () => {
          onPostEngagement?.(post);
        },
      },
    );
  }

  function openShareMenu() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canOpenShareMenu) {
      return;
    }

    setShareMenuVisible(true);
  }

  function beginShareAction(action: SharePostAction) {
    if (!user?.uid || sharingRef.current || post.visibility !== 'public') {
      if (!user?.uid) {
        router.push('/sign-in');
      }
      return false;
    }

    sharingRef.current = true;
    setSharing(true);
    setSharingAction(action);
    trackEvent('post_share_start', { method: action, source: 'photo_viewer' });
    return true;
  }

  function finishShareAction() {
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
  }

  async function recordSuccessfulShare(method: SharePostMethod) {
    try {
      const result = await recordPostShare(post.id);

      setOptimisticShareCount(result.shareCount);
      onPostEngagement?.(post);
      trackEvent('post_share_complete', { method, source: 'photo_viewer' });
    } catch {
      trackEvent('post_share_count_failed', {
        method,
        source: 'photo_viewer',
      });
    }
  }

  async function copyPostLink() {
    if (!beginShareAction('copy')) {
      return;
    }

    try {
      await copyTextToClipboard(shareUrl);
      await recordSuccessfulShare('clipboard');
      showSuccess('Post link copied.', 'Ready to share');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to copy that post link.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostWithDevice() {
    if (!beginShareAction('native')) {
      return;
    }

    try {
      const result = await shareLinkWithDevice({
        title: `${authorFullName} on Frienzzi`,
        text: post.body ?? 'View these photos on Frienzzi.',
        url: shareUrl,
      });

      if (result.cancelled) {
        return;
      }

      await recordSuccessfulShare(result.method);
      if (result.method === 'clipboard') {
        showSuccess('Post link copied.', 'Ready to share');
      }
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to share that post.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostToFacebook() {
    if (!beginShareAction('facebook')) {
      return;
    }

    try {
      await openFacebookShareDialog(shareUrl);
      await recordSuccessfulShare('facebook');
      showSuccess('Facebook share is ready.', 'Share opened');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to open Facebook sharing.');
    } finally {
      finishShareAction();
    }
  }

  function openReshareComposer() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canResharePost) {
      return;
    }

    setShareMenuVisible(false);
    setReshareBody('');
    setReshareMentions([]);
    setReshareComposerVisible(true);
  }

  async function publishResharePostOnFrienzzi() {
    if (!beginShareAction('reshare')) {
      return;
    }

    const currentUser = user;
    const trimmedBody = reshareBody.trim();
    const activeMentions = visibleProfileMentions(reshareMentions, trimmedBody);

    try {
      if (!currentUser?.uid) {
        throw new Error('Sign in is required.');
      }

      await createPost({
        authorFullName: currentUser.displayName?.trim().slice(0, 80) || null,
        authorUid: currentUser.uid,
        body: trimmedBody,
        profileMentions: activeMentions,
        reshareOfPost: post,
        visibility: 'public',
      });
      await recordSuccessfulShare('reshare');
      trackEvent('post_reshare_create', { source: 'photo_viewer' });
      showSuccess('Your reshare is live.', 'Reshared on Frienzzi');
      setReshareComposerVisible(false);
      setReshareBody('');
      setReshareMentions([]);
    } catch (error) {
      showError(error, 'Unable to reshare that post.');
    } finally {
      finishShareAction();
    }
  }

  return (
    <>
      <PhotoActions
        currentUid={user?.uid}
        onActorProfilePress={onActorProfilePress}
        post={post}
        reactionCounts={visibleReactionCounts}
        reactionDisabled={!user?.uid || reactionState.loading}
        reactionLoading={false}
        selectedReaction={visibleReaction}
        shareCount={visibleShareCount}
        totalReactions={totalVisibleReactions}
        onCommentPress={() => {
          if (!user?.uid) {
            router.push('/sign-in');
            return;
          }

          onCommentPress(post);
        }}
        onOpenShare={openShareMenu}
        onReaction={chooseReaction}
      />
      <ShareOptionsModal
        busy={sharing}
        options={[
          ...(canResharePost
            ? [
                {
                  description:
                    'Create a new public post with the original embedded.',
                  kind: 'reshare' as const,
                  loading: sharingAction === 'reshare',
                  onPress: openReshareComposer,
                  title: 'Reshare on Frienzzi',
                },
              ]
            : []),
          {
            description: "Open Facebook with Frienzzi's share dialog.",
            kind: 'facebook',
            loading: sharingAction === 'facebook',
            onPress: () => {
              void sharePostToFacebook();
            },
            title: 'Share to Facebook',
          },
          {
            description: nativeShareDescription(),
            kind: 'native',
            loading: sharingAction === 'native',
            onPress: () => {
              void sharePostWithDevice();
            },
            title: nativeShareTitle(),
          },
          {
            description: 'Copy the post link so you can paste it anywhere.',
            kind: 'copy',
            loading: sharingAction === 'copy',
            onPress: () => {
              void copyPostLink();
            },
            title: 'Copy link',
          },
        ]}
        subtitle="Choose where this public Frienzzi photo post goes."
        title="Share photos"
        visible={shareMenuVisible}
        onClose={() => {
          if (!sharing) {
            setShareMenuVisible(false);
          }
        }}
      />
      <ResharePhotoComposeModal
        body={reshareBody}
        currentUid={user?.uid}
        mentions={reshareMentions}
        post={post}
        posting={sharingAction === 'reshare'}
        visible={reshareComposerVisible}
        onBodyChange={setReshareBody}
        onClose={() => {
          if (!sharing) {
            setReshareComposerVisible(false);
          }
        }}
        onMentionsChange={setReshareMentions}
        onSubmit={() => {
          void publishResharePostOnFrienzzi();
        }}
      />
    </>
  );
}

export function PostPhotoViewerModal({
  hasMorePosts,
  initialPhotoIndex = 0,
  initialPost,
  initialPostId,
  loadingMorePosts,
  onClose,
  onActivePhotoIndexChange,
  onCommentPress,
  onLoadMorePosts,
  onPostEngagement,
  posts,
  actionRail,
  showPostActions = true,
  visible,
}: {
  actionRail?: ReactNode;
  hasMorePosts?: boolean;
  initialPhotoIndex?: number;
  initialPost?: PublicPost | null;
  initialPostId: string | null;
  loadingMorePosts?: boolean;
  onActivePhotoIndexChange?: (photoIndex: number, post: PublicPost) => void;
  onClose: () => void;
  onCommentPress: (post: PublicPost) => void;
  onLoadMorePosts?: () => void;
  onPostEngagement?: (post: PublicPost) => void;
  posts: PublicPost[];
  showPostActions?: boolean;
  visible: boolean;
}) {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const photoPosts = useMemo(
    () => photoPostsFromPosts(posts, initialPost),
    [initialPost, posts],
  );
  const initialIndex = Math.max(
    0,
    photoPosts.findIndex((post) => post.id === initialPostId),
  );
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [activePhotoIndex, setActivePhotoIndex] = useState(initialPhotoIndex);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [photoZoomed, setPhotoZoomed] = useState(false);
  const [createWebPortal, setCreateWebPortal] = useState<
    | ((
        children: ReactNode,
        container: Element | DocumentFragment,
      ) => ReactNode)
    | null
  >(null);
  const listRef = useRef<FlatList<PublicPost>>(null);
  const activeIndexRef = useRef(initialIndex);
  const trackedOpenKeyRef = useRef<string | null>(null);
  const photoPostsRef = useRef(photoPosts);
  const viewabilityConfigRef = useRef({ itemVisiblePercentThreshold: 72 });
  const activePost = photoPosts[activeIndex] ?? null;
  const activePhotos =
    activePost?.mediaItems?.filter((item) => item.kind === 'image') ?? [];

  const handleViewableItemsChangedRef = useRef(
    ({ viewableItems }: { viewableItems: ViewToken<PublicPost>[] }) => {
      const firstViewablePost = viewableItems.find(
        (viewableItem) => viewableItem.isViewable && viewableItem.item?.id,
      )?.item;

      if (!firstViewablePost) {
        return;
      }

      const nextIndex = photoPostsRef.current.findIndex(
        (post) => post.id === firstViewablePost.id,
      );

      if (nextIndex >= 0) {
        if (nextIndex === activeIndexRef.current) {
          return;
        }

        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
        setActivePhotoIndex(0);
        setPhotoZoomed(false);
        setCaptionExpanded(false);
        trackEvent('post_photo_viewer_swipe', { position: nextIndex });
      }
    },
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return undefined;
    }

    let active = true;

    void import('react-dom').then(({ createPortal }) => {
      if (active) {
        setCreateWebPortal(() => createPortal);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    photoPostsRef.current = photoPosts;
  }, [photoPosts]);

  useEffect(() => {
    if (!visible || !activePost) {
      trackedOpenKeyRef.current = null;
      return;
    }

    const openKey = `${activePost.id}:${initialPostId ?? ''}:${initialPhotoIndex}`;

    if (trackedOpenKeyRef.current === openKey) {
      return;
    }

    trackedOpenKeyRef.current = openKey;
    trackEvent('post_photo_viewer_open', {
      initial_photo_index: initialPhotoIndex,
      initial_position: activeIndex,
      queue_size: photoPosts.length,
    });
  }, [
    activeIndex,
    activePost,
    initialPhotoIndex,
    initialPostId,
    photoPosts.length,
    visible,
  ]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (
      !visible ||
      !hasMorePosts ||
      loadingMorePosts ||
      !onLoadMorePosts ||
      photoPosts.length === 0
    ) {
      return;
    }

    if (activeIndex >= Math.max(0, photoPosts.length - 2)) {
      onLoadMorePosts();
    }
  }, [
    activeIndex,
    hasMorePosts,
    loadingMorePosts,
    onLoadMorePosts,
    photoPosts.length,
    visible,
  ]);

  useLayoutEffect(() => {
    if (!visible) {
      setCaptionExpanded(false);
      setMinimized(false);
      setPhotoZoomed(false);
      return;
    }

    const currentPhotoPosts = photoPostsRef.current;
    const nextIndex = Math.max(
      0,
      currentPhotoPosts.findIndex((post) => post.id === initialPostId),
    );
    activeIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
    setActivePhotoIndex(initialPhotoIndex);
    setCaptionExpanded(false);
    setMinimized(false);
    setPhotoZoomed(false);
    if (currentPhotoPosts[nextIndex]) {
      onActivePhotoIndexChange?.(
        initialPhotoIndex,
        currentPhotoPosts[nextIndex],
      );
    }

    requestAnimationFrame(() => {
      listRef.current?.scrollToIndex({
        animated: false,
        index: nextIndex,
      });
    });
  }, [initialPhotoIndex, initialPostId, onActivePhotoIndexChange, visible]);

  const handleActivePhotoZoomChange = useCallback((zoomed: boolean) => {
    setPhotoZoomed(zoomed);
  }, []);

  function openAuthorProfile(post: PublicPost) {
    if (!user?.uid) {
      onClose();
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_photo_viewer',
      target_is_current_user: post.authorUid === user.uid,
    });
    setMinimized(true);
    requestAnimationFrame(() => {
      openUserProfile({ currentUid: user.uid, targetUid: post.authorUid });
    });
  }

  function openReactionActorProfile(targetUid: string) {
    if (!user?.uid) {
      onClose();
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_photo_reaction_details',
      target_is_current_user: targetUid === user.uid,
    });
    setMinimized(true);
    requestAnimationFrame(() => {
      openUserProfile({ currentUid: user.uid, targetUid });
    });
  }

  if (!visible || !activePost || photoPosts.length === 0) {
    return null;
  }

  if (minimized) {
    const activePhoto = activePhotos[activePhotoIndex] ?? activePhotos[0];
    const miniPlayerOverlay = (
      <View pointerEvents="box-none" style={styles.minimizedRoot}>
        <Pressable
          accessibilityLabel="Return to photos"
          accessibilityRole="button"
          onPress={() => setMinimized(false)}
          style={({ pressed }) => [
            styles.miniPlayer,
            pressed ? styles.miniPlayerPressed : null,
          ]}
        >
          {activePhoto ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{
                uri:
                  activePhoto.thumbnail?.downloadURL ?? activePhoto.downloadURL,
              }}
              style={styles.miniPlayerImage}
            />
          ) : (
            <View style={styles.miniPlayerFallback}>
              <Text style={styles.photoFallbackText}>Photo</Text>
            </View>
          )}
          <Pressable
            accessibilityLabel="Close minimized photos"
            accessibilityRole="button"
            hitSlop={8}
            onPress={(event) => {
              event.stopPropagation();
              onClose();
            }}
            style={({ pressed }) => [
              styles.miniPlayerClose,
              pressed ? styles.miniPlayerClosePressed : null,
            ]}
          >
            <X color={colors.surface} size={14} strokeWidth={2.8} />
          </Pressable>
          <View style={styles.miniPlayerOverlay}>
            <Text numberOfLines={1} style={styles.miniPlayerText}>
              Back to photos
            </Text>
          </View>
        </Pressable>
      </View>
    );

    if (
      Platform.OS === 'web' &&
      typeof document !== 'undefined' &&
      createWebPortal
    ) {
      return <>{createWebPortal(miniPlayerOverlay, document.body)}</>;
    }

    return (
      <Modal transparent visible onRequestClose={onClose}>
        {miniPlayerOverlay}
      </Modal>
    );
  }

  return (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      presentationStyle="fullScreen"
      visible={visible}
    >
      <View style={styles.viewerRoot}>
        <FlatList
          key={initialPostId ?? 'post-photo-viewer'}
          ref={listRef}
          data={photoPosts}
          decelerationRate="fast"
          getItemLayout={(_, index) => ({
            index,
            length: height,
            offset: height * index,
          })}
          initialScrollIndex={initialIndex}
          keyExtractor={(post) => post.id}
          onEndReached={() => {
            if (hasMorePosts && !loadingMorePosts) {
              onLoadMorePosts?.();
            }
          }}
          onEndReachedThreshold={0.75}
          onScrollToIndexFailed={(info) => {
            requestAnimationFrame(() => {
              listRef.current?.scrollToOffset({
                animated: false,
                offset: info.averageItemLength * info.index,
              });
            });
          }}
          onViewableItemsChanged={handleViewableItemsChangedRef.current}
          pagingEnabled
          scrollEnabled={!photoZoomed}
          renderItem={({ index, item }) => (
            <PhotoSlide
              activePhotoIndex={index === activeIndex ? activePhotoIndex : 0}
              height={height}
              onPhotoIndexChange={(nextPhotoIndex) => {
                setActivePhotoIndex(nextPhotoIndex);
                if (index === activeIndex) {
                  onActivePhotoIndexChange?.(nextPhotoIndex, item);
                }
              }}
              onZoomChange={
                index === activeIndex ? handleActivePhotoZoomChange : undefined
              }
              post={item}
              width={width}
            />
          )}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          snapToAlignment="start"
          snapToInterval={height}
          ListFooterComponent={
            loadingMorePosts ? (
              <View style={[styles.loadingMoreSlide, { height }]}>
                <ActivityIndicator color={colors.surface} />
                <Text style={styles.loadingMoreText}>Loading more photos</Text>
              </View>
            ) : null
          }
          viewabilityConfig={viewabilityConfigRef.current}
        />
        <View
          pointerEvents="box-none"
          style={[
            styles.safeOverlay,
            {
              bottom: Math.max(insets.bottom, spacing.md),
              left: Math.max(insets.left, 0),
              right: Math.max(insets.right, 0),
              top: Math.max(insets.top, spacing.md),
            },
          ]}
        >
          <PhotoOverlay
            actionsVisible={showPostActions || Boolean(actionRail)}
            captionExpanded={captionExpanded}
            currentUid={user?.uid}
            photoCount={Math.max(activePhotos.length, 1)}
            photoIndex={activePhotoIndex}
            post={activePost}
            onClose={onClose}
            onMinimize={() => {
              trackEvent('post_photo_viewer_minimize');
              setMinimized(true);
            }}
            onOpenAuthor={() => openAuthorProfile(activePost)}
            onToggleCaption={() => setCaptionExpanded((current) => !current)}
          />
          {showPostActions ? (
            <ActivePhotoControls
              onActorProfilePress={openReactionActorProfile}
              post={activePost}
              onCommentPress={(post) => {
                setMinimized(true);
                onCommentPress(post);
              }}
              onPostEngagement={onPostEngagement}
            />
          ) : (
            actionRail
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  authorButton: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    gap: spacing.sm,
    minWidth: 0,
  },
  authorName: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
  },
  authorText: {
    minWidth: 0,
  },
  avatarImage: {
    borderRadius: 18,
    height: 36,
    width: 36,
  },
  avatarText: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
  },
  bottomBar: {
    gap: spacing.sm,
  },
  caption: {
    color: colors.surface,
    fontSize: 15,
    lineHeight: 21,
  },
  captionMention: {
    color: colors.surface,
    fontWeight: '900',
  },
  closeButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.62)',
    borderRadius: 21,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  commentButton: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(13, 9, 43, 0.62)',
    borderRadius: 999,
    flexDirection: 'row',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  commentButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '900',
  },
  counterBadge: {
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(13, 9, 43, 0.62)',
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
  },
  counterText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  overlay: {
    bottom: 0,
    justifyContent: 'space-between',
    left: 0,
    padding: spacing.md,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  photo: {
    height: '100%',
    width: '100%',
  },
  photoPage: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
  },
  postDate: {
    color: colors.surfaceTint,
    fontSize: 12,
    fontWeight: '700',
  },
  root: {
    backgroundColor: colors.text,
    flex: 1,
  },
  slide: {
    backgroundColor: colors.text,
  },
  topBar: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  actionCluster: {
    alignItems: 'center',
    gap: 2,
  },
  actionCount: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  actionsRail: {
    alignItems: 'center',
    bottom: 206,
    gap: spacing.sm,
    position: 'absolute',
    right: spacing.md,
    width: 50,
  },
  authorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  viewerIdentity: {
    flex: 1,
  },
  authorTextBlock: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  viewerAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minWidth: 0,
  },
  bottomContent: {
    bottom: 0,
    gap: spacing.sm,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  bottomOverlay: {
    backgroundColor: 'rgba(0,0,0,0.32)',
    borderColor: 'rgba(255,255,255,0.1)',
    borderRadius: 16,
    borderWidth: 1,
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    maxWidth: 560,
    padding: spacing.sm,
    paddingRight: 86,
  },
  bottomOverlayNoActions: {
    paddingRight: spacing.sm,
  },
  captionPressable: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  captionScroll: {
    maxHeight: 44,
  },
  captionScrollExpanded: {
    maxHeight: 168,
  },
  composeActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  composeBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.38)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  composeButton: {
    flex: 1,
  },
  composeCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    maxHeight: '92%',
    maxWidth: 520,
    padding: spacing.lg,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  composeCloseButton: {
    alignItems: 'center',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  composeCounter: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    marginTop: -spacing.sm,
    textAlign: 'right',
  },
  composeHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  composeInput: {
    minHeight: 94,
  },
  composeScroll: {
    flexGrow: 0,
  },
  composeScrollContent: {
    gap: spacing.md,
  },
  composeSubtitle: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  composeTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  composeTitleBlock: {
    flex: 1,
    gap: 3,
  },
  followButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 84,
    paddingHorizontal: spacing.sm,
  },
  followButtonDisabled: {
    opacity: 0.64,
  },
  followButtonFollowing: {
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderColor: 'rgba(255,255,255,0.45)',
  },
  followButtonPressed: {
    transform: [{ scale: 0.97 }],
  },
  followButtonText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  followButtonTextFollowing: {
    color: colors.surface,
  },
  loadingMoreSlide: {
    alignItems: 'center',
    backgroundColor: '#000000',
    gap: spacing.sm,
    justifyContent: 'center',
  },
  loadingMoreText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '800',
    opacity: 0.76,
  },
  minimizedRoot: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  miniPlayer: {
    backgroundColor: '#000000',
    borderColor: 'rgba(255,255,255,0.32)',
    borderRadius: 14,
    borderWidth: 1,
    bottom: spacing.lg,
    height: 112,
    left: spacing.lg,
    overflow: 'hidden',
    position: 'absolute',
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 18,
    width: 150,
  },
  miniPlayerClose: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.62)',
    borderColor: 'rgba(255,255,255,0.28)',
    borderRadius: 999,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    position: 'absolute',
    right: 6,
    top: 6,
    width: 24,
  },
  miniPlayerClosePressed: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  miniPlayerFallback: {
    alignItems: 'center',
    backgroundColor: '#000000',
    flex: 1,
    justifyContent: 'center',
  },
  miniPlayerImage: {
    height: '100%',
    width: '100%',
  },
  miniPlayerOverlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.46)',
    bottom: 0,
    flexDirection: 'row',
    gap: spacing.xs,
    left: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    position: 'absolute',
    right: 0,
  },
  miniPlayerPressed: {
    transform: [{ scale: 0.98 }],
  },
  miniPlayerText: {
    color: colors.surface,
    flex: 1,
    fontSize: 12,
    fontWeight: '900',
  },
  moreText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
    marginTop: 2,
    opacity: 0.9,
  },
  overlayButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.38)',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  overlayButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  overlayButtonPressedDark: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  photoCounterButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.38)',
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 44,
    paddingHorizontal: spacing.md,
  },
  photoCounterText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  photoFallbackText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  railButton: {
    alignItems: 'center',
    borderRadius: 999,
    gap: 2,
    justifyContent: 'center',
    paddingVertical: 2,
    width: 50,
  },
  railButtonPressed: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  railIconCircle: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.32)',
    borderColor: 'rgba(255,255,255,0.16)',
    borderRadius: 999,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  resharePreview: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  resharePreviewBody: {
    color: colors.secondary,
    fontSize: 13,
    lineHeight: 19,
  },
  resharePreviewEyebrow: {
    color: colors.primary,
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  resharePreviewTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  safeOverlay: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  topLeftActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  topOverlay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: spacing.md,
    position: 'absolute',
    right: spacing.md,
    top: 0,
  },
  topReactionButton: {
    alignItems: 'center',
    borderRadius: 24,
    justifyContent: 'center',
    minHeight: 34,
    paddingVertical: 2,
    width: 50,
  },
  topReactionBubble: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderColor: 'rgba(255,255,255,0.18)',
    borderRadius: 999,
    borderWidth: 1,
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  topReactionBubbleOverlap: {
    marginLeft: -8,
  },
  topReactionEmoji: {
    fontSize: 18,
    lineHeight: 22,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 4,
  },
  topReactionStack: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    minWidth: 44,
  },
  viewerAuthor: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  viewerAvatar: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.surface,
    borderRadius: 22,
    borderWidth: 2,
    height: 44,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 44,
  },
  viewerAvatarImage: {
    height: '100%',
    width: '100%',
  },
  viewerAvatarText: {
    color: colors.secondary,
    fontSize: 17,
    fontWeight: '900',
  },
  viewerCaption: {
    color: colors.surface,
    fontSize: 14,
    lineHeight: 20,
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  viewerCaptionMention: {
    color: colors.primary,
    fontWeight: '900',
  },
  viewerMeta: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 12,
    fontWeight: '800',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 4,
  },
  viewerRoot: {
    backgroundColor: '#000000',
    flex: 1,
  },
});
